"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { Store, Search, Globe, Menu, X, Sparkles, Sun, Moon, Calendar, Crown, LayoutDashboard, ClipboardList, LogOut, UserCircle, Flame, Trash2, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { auth } from "@/lib/firebase/client";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/components/providers/theme-provider";

const LANGUAGES = [
  { code: 'he', label: 'עברית' },
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
];

export function Navbar() {
  const { user, userData, loading, activeRole, setActiveRole } = useAuth();
  const { t, i18n } = useTranslation('common');
  const { resolvedTheme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLangChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;
    i18n.changeLanguage(newLang);
    // Intercept and update URL for SEO/Sharing
    const url = new URL(window.location.href);
    url.searchParams.set('lang', newLang);
    window.history.replaceState({}, '', url.toString());
  }, [i18n]);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setTheme]);

  const closeMobile = useCallback(() => { setMobileOpen(false); setUserMenuOpen(false); }, []);

  const isOwnerOrAdmin = userData?.role === 'owner' || userData?.role === 'admin';
  const isAdminRole = userData?.role === 'admin';
  const showBusinessControls = activeRole === 'owner' || activeRole === 'admin';



  /* ------ Desktop Nav Links ------ */
  const navLinks = (
    <>
      <Link href="/catalog" onClick={closeMobile} className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
        <Search className="w-4 h-4 opacity-70" /> {t('nav.catalog')}
      </Link>
      <Link href="/tender" onClick={closeMobile} className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
        <Sparkles className="w-4 h-4 opacity-70" /> {t('nav.tender')}
      </Link>
      <Link href="/business" onClick={closeMobile} className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
        <Store className="w-4 h-4 opacity-70" /> {t('nav.for_business')}
      </Link>
      <Link href="/blog" onClick={closeMobile} className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-book-open opacity-70"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
        {t('nav.blog')}
      </Link>
      <Link href="/specials" onClick={closeMobile} className="text-sm font-semibold text-rose-500 hover:text-rose-600 transition-colors flex items-center gap-2">
          <Flame className="w-4 h-4" /> {t('nav.deals')}
        </Link>
      <Link href="/about" onClick={closeMobile} className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
        <Info className="w-4 h-4 opacity-70" /> {t('nav.about')}
      </Link>
    </>
  );

  const langSwitcher = (
    <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground bg-secondary/60 px-2.5 py-1.5 rounded-full border border-border/50">
      <Globe className="w-3.5 h-3.5" />
      <select 
        value={i18n.language} 
        onChange={handleLangChange}
        aria-label={t('nav.select_language') || 'Language'}
        className="bg-transparent border-none outline-none cursor-pointer appearance-none text-foreground font-semibold text-xs"
      >
        {LANGUAGES.map(lang => (
          <option key={lang.code} value={lang.code} className="text-foreground">{lang.label}</option>
        ))}
      </select>
    </div>
  );

  const themeToggle = (
    <button
      onClick={toggleTheme}
      className="w-9 h-9 rounded-full bg-secondary/60 border border-border/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
      aria-label="Toggle theme"
    >
      {resolvedTheme === 'dark' ? (
        <Sun className="w-4 h-4 text-amber-400" />
      ) : (
        <Moon className="w-4 h-4 text-indigo-500" />
      )}
    </button>
  );

  /* ------ Desktop Auth Block (compact) ------ */
  // Prominent admin button — visible ONLY to the admin role (clients never see it).
  const adminButton = isAdminRole ? (
    <Link
      href="/admin"
      aria-label={t('admin.title')}
      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 font-bold text-sm hover:bg-amber-500/20 transition-all"
    >
      <Crown className="w-4 h-4" />
      <span className="hidden lg:inline">{t('admin.title')}</span>
    </Link>
  ) : null;
  const desktopAuthBlock = (
    <>
      {loading ? (
        <div className="w-24 h-10 animate-pulse bg-muted rounded-full"></div>
      ) : user ? (
        <div className="flex items-center gap-2">
          {/* User avatar + dropdown trigger */}
          <div className="relative group">
            {userMenuOpen && <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setUserMenuOpen(false)} />}
            <button aria-label="User menu" aria-haspopup="true" aria-expanded={userMenuOpen} onClick={() => setUserMenuOpen((o) => !o)} className="relative z-50 flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary/60 border border-border/50 hover:border-primary/30 transition-all">
              <div className="w-7 h-7 rounded-full bg-linear-to-br from-primary to-pink-400 flex items-center justify-center text-white text-xs font-black">
                {user.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex flex-col items-start">
                <span className="text-xs font-bold text-foreground leading-tight">{user.displayName || user.email?.split('@')[0]}</span>
                <span className="text-[10px] text-muted-foreground font-semibold leading-tight">
                  {activeRole === 'owner' ? t('nav.role_business') : activeRole === 'admin' ? t('nav.role_admin') : t('nav.role_client')}
                </span>
              </div>
            </button>

            {/* Dropdown */}
            <div className={`absolute end-0 top-full mt-1 w-56 bg-background/95 backdrop-blur-xl border border-border/60 rounded-2xl shadow-2xl p-2 ${userMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'} group-hover:opacity-100 group-hover:visible transition-all duration-200 z-100`}>
              
              {/* My Requests - always visible */}
              <Link href="/my-requests" onClick={closeMobile}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary/5 text-foreground transition-colors">
                <ClipboardList className="w-4 h-4 text-emerald-500" /> {t('nav.my_requests')}
              </Link>

              {/* Dashboard — only for owner/admin in business mode */}
              {showBusinessControls && (
                <>
                  <Link href="/dashboard" onClick={closeMobile}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary/5 text-foreground transition-colors">
                    <LayoutDashboard className="w-4 h-4 text-primary" /> {t('nav.dashboard')}
                  </Link>
                  <Link href="/dashboard/calendar" onClick={closeMobile}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary/5 text-foreground transition-colors">
                    <Calendar className="w-4 h-4 text-blue-500" /> {t('nav.today_queue')}
                  </Link>
                </>
              )}

              {/* Admin panel — ONLY for admin role (not owner) */}
              {isAdminRole && (
                <Link href="/admin" onClick={closeMobile}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-500/5 text-amber-600 transition-colors">
                  <Crown className="w-4 h-4" /> {t('admin.title')}
                </Link>
              )}

              {/* Become a specialist — for regular clients only */}
              {!isOwnerOrAdmin && (
                <Link href="/business/register" onClick={closeMobile}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary/5 text-primary transition-colors">
                  <Sparkles className="w-4 h-4" /> {t('nav.expert')}
                </Link>
              )}

              {/* Mode switcher — only for owner/admin */}
              {isOwnerOrAdmin && (
                <>
                  <div className="h-px bg-border my-1.5" />
                  <button
                    onClick={() => setActiveRole(activeRole === 'client' ? (userData?.role === 'admin' ? 'admin' : 'owner') : 'client')}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-secondary w-full text-start text-muted-foreground transition-colors"
                  >
                    {activeRole === 'client' ? <Store className="w-4 h-4" /> : <UserCircle className="w-4 h-4" />}
                    {activeRole === 'client' ? t('nav.return_to_business') : t('nav.as_buyer')}
                  </button>
                </>
              )}

              <div className="h-px bg-border my-1.5" />
              <Link href="/account/delete" onClick={closeMobile}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-red-500/5 text-muted-foreground hover:text-red-500 transition-colors">
                <Trash2 className="w-4 h-4" /> {t('account.delete_menu')}
              </Link>
              <button 
                onClick={() => { auth.signOut(); closeMobile(); }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-red-500/5 w-full text-start text-red-500 transition-colors"
              >
                <LogOut className="w-4 h-4" /> {t('dashboard.logout')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <Link href="/login" onClick={closeMobile} className="flex items-center gap-2 text-sm font-bold bg-primary text-primary-foreground px-6 py-2.5 rounded-full hover:scale-105 transition-all shadow-lg shadow-primary/20">
           {t('nav.login')}
        </Link>
      )}
    </>
  );

  /* ------ Mobile Auth Block (full list) ------ */
  const mobileAuthBlock = (
    <>
      {loading ? (
        <div className="w-full h-10 animate-pulse bg-muted rounded-xl"></div>
      ) : user ? (
        <div className="flex flex-col gap-1">
          {/* User info */}
          <div className="flex items-center gap-3 px-4 py-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary to-pink-400 flex items-center justify-center text-white font-black">
              {user.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="font-bold text-sm text-foreground">{user.displayName || user.email?.split('@')[0]}</p>
              <p className="text-xs text-muted-foreground font-semibold">
                {activeRole === 'owner' ? t('nav.role_business') : activeRole === 'admin' ? t('nav.role_admin') : t('nav.role_client')}
              </p>
            </div>
          </div>

          <Link href="/my-requests" onClick={closeMobile}
            className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground hover:bg-primary/5 transition-colors">
            <ClipboardList className="w-5 h-5 text-emerald-500" /> {t('nav.my_requests')}
          </Link>

          {showBusinessControls && (
            <>
              <Link href="/dashboard" onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground hover:bg-primary/5 transition-colors">
                <LayoutDashboard className="w-5 h-5 text-primary" /> {t('nav.dashboard')}
              </Link>
              <Link href="/dashboard/calendar" onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground hover:bg-primary/5 transition-colors">
                <Calendar className="w-5 h-5 text-blue-500" /> {t('nav.today_queue')}
              </Link>
            </>
          )}

          {isAdminRole && (
            <Link href="/admin" onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-amber-600 hover:bg-amber-500/5 transition-colors">
              <Crown className="w-5 h-5" /> {t('admin.title')}
            </Link>
          )}

          {!isOwnerOrAdmin && (
            <Link href="/business/register" onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-primary hover:bg-primary/5 transition-colors">
              <Sparkles className="w-5 h-5" /> {t('nav.expert')}
            </Link>
          )}

          {isOwnerOrAdmin && (
            <button
              onClick={() => { setActiveRole(activeRole === 'client' ? (userData?.role === 'admin' ? 'admin' : 'owner') : 'client'); closeMobile(); }}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-muted-foreground hover:bg-secondary/60 transition-colors w-full text-start"
            >
              {activeRole === 'client' ? <Store className="w-5 h-5" /> : <UserCircle className="w-5 h-5" />}
              {activeRole === 'client' ? t('nav.return_to_business') : t('nav.as_buyer')}
            </button>
          )}

          <div className="h-px bg-border my-1" />
          <Link href="/account/delete" onClick={closeMobile}
            className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-muted-foreground hover:text-red-500 hover:bg-red-500/5 transition-colors">
            <Trash2 className="w-5 h-5" /> {t('account.delete_menu')}
          </Link>
          <button 
            onClick={() => { auth.signOut(); closeMobile(); }}
            className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-red-500 hover:bg-red-500/5 transition-colors w-full text-start"
          >
            <LogOut className="w-5 h-5" /> {t('dashboard.logout')}
          </button>
        </div>
      ) : (
        <Link href="/login" onClick={closeMobile} className="flex items-center justify-center gap-2 text-base font-bold bg-primary text-primary-foreground px-6 py-3 rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 mx-4">
           {t('nav.login')}
        </Link>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-xl border-b border-border/60 shadow-sm">
      <div className="container mx-auto flex h-14 md:h-16 items-center px-4 md:px-6 justify-between max-w-[1400px]">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 md:w-9 md:h-9 bg-linear-to-br from-primary to-pink-400 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-primary/20">
            <span className="text-lg md:text-xl font-black text-white">Y</span>
          </div>
          <span className="text-lg md:text-xl font-black tracking-tight">
            Yofi<span className="text-primary">Place</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex gap-6 items-center">
          {navLinks}
          <div className="h-5 w-px bg-border"></div>
          {langSwitcher}
          {themeToggle}
          {adminButton}
          <div className="h-5 w-px bg-border"></div>
          {desktopAuthBlock}
        </nav>

        {/* Mobile: theme + lang + hamburger */}
        <div className="flex md:hidden items-center gap-2">
          {themeToggle}
          {langSwitcher}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-secondary/60 border border-border/50 text-foreground hover:bg-secondary transition-colors"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile slide-out drawer */}
      {mobileOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-40 md:hidden"
            onClick={closeMobile}
          />
          <div className="fixed top-14 inset-x-0 z-50 md:hidden animate-in slide-in-from-top-2 duration-200">
            <div className="bg-background/95 backdrop-blur-2xl border-b border-border shadow-2xl mx-2 rounded-2xl mt-1 overflow-hidden">
              <nav className="flex flex-col p-4 gap-1">
                <Link href="/catalog" onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground hover:bg-primary/5 transition-colors">
                  <Search className="w-5 h-5 text-primary" /> {t('nav.catalog')}
                </Link>
                <Link href="/tender" onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground hover:bg-primary/5 transition-colors">
                  <Sparkles className="w-5 h-5 text-primary" /> {t('nav.tender')}
                </Link>
                <Link href="/business" onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground hover:bg-primary/5 transition-colors">
                  <Store className="w-5 h-5 text-primary" /> {t('nav.for_business')}
                </Link>
                <Link href="/blog" onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-foreground hover:bg-primary/5 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-book-open text-primary"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg> 
                  {t('nav.blog')}
                </Link>
                <Link href="/specials" onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold text-rose-500 hover:bg-rose-500/5 transition-colors">
                  <Flame className="w-5 h-5" /> {t('nav.deals')}
                </Link>
                
                <div className="h-px bg-border my-2" />
                
                {mobileAuthBlock}
              </nav>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
